import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Camera, Send, Paperclip, X, Loader } from 'lucide-react';
import html2canvas from 'html2canvas-pro';
import { createPortal } from 'react-dom';
import { useAuth } from '../../App';
import { debugCollector } from '../../utils/DebugCollector';
import ImageLightbox from '../ImageLightbox';
import {
  addStationFeedbackComment,
  addFeedbackComment,
  getFeedbackImageUrl,
  type Feedback,
  type FeedbackComment,
  type FeedbackStatus,
} from '../../api/client';

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  OPEN: 'Offen',
  RUCKFRAGE: 'Rueckfrage',
  DONE: 'Erledigt',
  REJECTED: 'Abgelehnt',
};

const STATUS_COLORS: Record<FeedbackStatus, string> = {
  OPEN: 'text-akzent bg-akzent-tief border-akzent-tief',
  RUCKFRAGE: 'text-stand bg-flaeche-2 border-linie',
  DONE: 'text-lauf bg-flaeche-2 border-linie',
  REJECTED: 'text-tinte-3 bg-flaeche-2 border-linie',
};

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}

interface ChatThreadProps {
  feedback: Feedback;
  onBack: () => void;
  onCommentAdded: () => void;
}

export default function ChatThread({ feedback, onBack, onCommentAdded }: ChatThreadProps) {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isStation = !!user?.stationId;
  const myId = user?.userId || user?.stationId || '';

  // Visible comments (no DEBUG_DATA)
  const visibleComments = feedback.comments.filter((c) => c.type !== 'DEBUG_DATA');

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visibleComments.length]);

  // Auto-resize textarea
  const handleTextChange = (val: string) => {
    setText(val);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleScreenshot = async () => {
    if (capturing) return;
    setCapturing(true);

    const drawer = document.querySelector('[data-feedback-drawer]') as HTMLElement | null;
    try {
      // Ghost-Mode: Drawer verstecken
      if (drawer) {
        drawer.style.visibility = 'hidden';
        await new Promise((r) => setTimeout(r, 150));
      }

      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: false,
        scale: 1,
      });

      // Drawer sofort wieder zeigen
      if (drawer) drawer.style.visibility = 'visible';

      canvas.toBlob(
        (blob: Blob | null) => {
          if (!blob) {
            console.error('Canvas-Export fehlgeschlagen: Blob ist null.');
            setCapturing(false);
            return;
          }
          const file = new File([blob], `screen_${Date.now()}.jpg`, { type: 'image/jpeg' });
          setAttachments((prev) => [...prev, file]);
          setCapturing(false);
        },
        'image/jpeg',
        0.8,
      );
    } catch (error: any) {
      console.error('Screenshot fehlgeschlagen:', error);
      if (drawer) drawer.style.visibility = 'visible';
      setCapturing(false);
    }
  };

  const handleSend = async () => {
    if (!text.trim() && attachments.length === 0) return;
    if (!user) return;

    setSending(true);
    try {
      const debugData = JSON.stringify(debugCollector.getSnapshot());
      const total = Math.max(1, attachments.length);

      const sendComment = async (msg?: string, img?: File, dbg?: string) => {
        if (isStation && user.stationId) {
          await addStationFeedbackComment(user.stationId, feedback.id, {
            message: msg,
            image: img,
            debugData: dbg,
          });
        } else {
          await addFeedbackComment(feedback.id, {
            message: msg,
            image: img,
            debugData: dbg,
          });
        }
      };

      if (total > 1) setSendProgress(`Sende 1 von ${total}...`);
      await sendComment(text.trim() || undefined, attachments[0] || undefined, debugData);

      for (let i = 1; i < attachments.length; i++) {
        setSendProgress(`Sende ${i + 1} von ${total}...`);
        await sendComment(undefined, attachments[i]);
      }

      setText('');
      setAttachments([]);
      setSendProgress('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      onCommentAdded();
    } catch (e: any) {
      console.error('Comment failed:', e);
    } finally {
      setSending(false);
      setSendProgress('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderComment = (comment: FeedbackComment) => {
    if (comment.type === 'SYSTEM_EVENT') {
      return (
        <div key={comment.id} className="flex justify-center py-1.5">
          <span className="text-[11px] text-tinte-3 bg-flaeche-2 px-2.5 py-0.5 rounded-full">
            {comment.message}
          </span>
        </div>
      );
    }

    const isMe = comment.authorId === myId || comment.authorId === feedback.submittedById;
    const authorName = comment.author?.displayName || 'System';
    const initial = (authorName[0] || '?').toUpperCase();

    return (
      <div key={comment.id} className={`flex gap-2 ${isMe ? 'flex-row-reverse' : ''}`}>
        {/* Avatar */}
        <div
          className={`w-7 h-7 rounded-full text-akzent-satz text-xs font-bold flex items-center justify-center shrink-0 ${
            isMe ? 'bg-lauf' : 'bg-akzent'
          }`}
        >
          {initial}
        </div>

        {/* Bubble */}
        <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
          <div className={`text-[11px] mb-0.5 ${isMe ? 'text-right' : ''} text-tinte-3`}>
            <span className="font-medium text-tinte-2">{authorName}</span>
            <span className="ml-1.5">{formatTime(comment.createdAt)}</span>
          </div>
          <div
            className={`rounded-xl px-3 py-2 border ${
              isMe
                ? 'bg-flaeche-2 border-linie rounded-tr-sm'
                : 'bg-akzent-tief border-akzent-tief rounded-tl-sm'
            }`}
          >
            {comment.message && (
              <p className="text-sm text-tinte-2 whitespace-pre-wrap break-words">
                {comment.message}
              </p>
            )}
            {comment.imagePath && (
              <button
                onClick={() =>
                  setLightboxSrc(
                    getFeedbackImageUrl(comment.imagePath!, feedback.stationId || undefined),
                  )
                }
                className="block mt-1 cursor-pointer"
              >
                <img
                  src={getFeedbackImageUrl(comment.imagePath, feedback.stationId || undefined)}
                  alt="Bild"
                  className="max-w-[200px] max-h-[140px] rounded-lg border border-linie hover:border-akzent transition-colors"
                />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" data-feedback-drawer>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-flaeche-2 shrink-0">
        <button
          onClick={onBack}
          className="p-1 text-tinte-3 hover:text-tinte-2 rounded transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-tinte-3">#{feedback.number}</span>
            <span className="text-sm font-semibold text-tinte truncate">{feedback.title}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${STATUS_COLORS[feedback.status]}`}
            >
              {STATUS_LABELS[feedback.status]}
            </span>
            <span className="text-[11px] text-tinte-3">
              {feedback.submittedBy?.displayName || 'Unbekannt'}
              {feedback.station?.name ? ` · ${feedback.station.name}` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {visibleComments.length === 0 ? (
          <p className="text-sm text-tinte-3 text-center py-8">Noch keine Nachrichten</p>
        ) : (
          visibleComments.map(renderComment)
        )}
      </div>

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="px-4 py-2 border-t bg-flaeche-2 shrink-0">
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 p-2 bg-flaeche-2 border border-linie rounded-md"
              >
                <div className="relative w-10 h-10 shrink-0">
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Preview"
                    className="w-full h-full object-cover rounded bg-flaeche"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-tinte-3 truncate max-w-[100px]">
                    {file.name}
                  </span>
                  <button
                    onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                    className="text-[10px] text-stoer hover:underline text-left"
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="px-3 py-2.5 border-t bg-flaeche shrink-0">
        <div className="flex items-end gap-2">
          {/* Screenshot */}
          <button
            onClick={handleScreenshot}
            disabled={capturing}
            className="p-2 rounded-full hover:bg-flaeche-2 text-tinte-3 transition-colors shrink-0"
            title="Screenshot aufnehmen"
          >
            {capturing ? (
              <Loader className="w-4 h-4 animate-spin text-stand" />
            ) : (
              <Camera className="w-4 h-4" />
            )}
          </button>

          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setAttachments((prev) => [...prev, f]);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-full hover:bg-flaeche-2 text-tinte-3 transition-colors shrink-0"
            title="Bild anhaengen"
          >
            <Paperclip className="w-4 h-4" />
          </button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nachricht schreiben..."
            rows={1}
            className="flex-1 resize-none border border-linie rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-linie-2 placeholder:text-tinte-3"
          />

          {/* Send */}
          <button
            onClick={handleSend}
            disabled={sending || (!text.trim() && attachments.length === 0)}
            className="p-2 text-tinte bg-flaeche rounded-lg hover:bg-flaeche-2 transition-colors disabled:opacity-40 disabled:hover:bg-flaeche shrink-0"
            title="Senden (Ctrl+Enter)"
          >
            <Send className={`w-4 h-4 ${sending ? 'animate-pulse' : ''}`} />
          </button>
        </div>
        <p className="text-[10px] text-tinte-3 mt-1 text-right">
          {sendProgress || 'Ctrl+Enter zum Senden'}
        </p>
      </div>

      {/* Lightbox */}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
